import { Component, h } from 'preact'
import axios from 'axios'
import Header from '../../control-components/header'
import BadgeSelector from './BadgeSelector'
import Panel from '../../control-components/panel'
import tracker from '../../components/tracker/publish'
import AlertComponent from '../../control-components/ui-components/AlertComponent'
import { BadgeList } from './index'
import UserSelector from '../user-selector/UserSelector'
import { User } from '../user-selector/User'
import Loader from '../../control-components/ui-components/Loader'

interface Props {
  awarderId: number,
  awarderUsername: string,
  awarderRole: string
}
interface State {
  badgesList: BadgeList | null,
  selectedBadge: string | null,
  users: Array<User>,
  backendErrors: {
    errorMessage: string | null,
    messageType: string | null
  },
  errors: {
    badge?: string,
    usersInput?: string
  },
  loading: boolean
}

const MAX_NUMBER_USERS = 50

const errorMessages = {
  noBadge: 'Please select a badge',
  noContent: 'Please add at least one user',
  userLimitExceeded: `Please select a maximum of ${MAX_NUMBER_USERS} users`
}

class AwardBadge extends Component<Props, State> {
  constructor () {
    super()
    this.state = {
      badgesList: null,
      selectedBadge: null,
      users: [],
      backendErrors: {
        errorMessage: null,
        messageType: null
      },
      errors: {},
      loading: false
    }

    this.awardBadge = this.awardBadge.bind(this)
    this.onUsersChange = this.onUsersChange.bind(this)
    this.onBadgeSelected = this.onBadgeSelected.bind(this)
    this.trackBadgeAwardedEvent = this.trackBadgeAwardedEvent.bind(this)
  }

  async componentDidMount () {
    const response = await axios.get('/settings/badge/getBadgesList')
    this.setState({ badgesList: response.data })
  }

  async awardBadge () {
    this.setState({ backendErrors: { errorMessage: null, messageType: null }, errors: {} })
    const { selectedBadge, users } = this.state

    const userLimitExceeded = users.length > MAX_NUMBER_USERS
    const noUserSelected = users.length === 0

    if (!selectedBadge || noUserSelected || userLimitExceeded) {
      this.handleErrors(!selectedBadge, noUserSelected, userLimitExceeded)
      return
    }

    this.setState({ loading: true })

    await axios.post('/settings/badge/award', {
      badgeId: selectedBadge,
      userNames: users.map((user: User) => user.name)
    }).then((resolve) => {
      if (resolve.data.error) {
        this.setState({ backendErrors: { errorMessage: resolve.data.error, messageType: 'error' }, loading: false })
      } else {
        users.forEach((user: User) => {
          this.trackBadgeAwardedEvent(selectedBadge, user.id, user.name, user.mainRole, users.length)
        })
        window.location.assign('/settings/badge/overview?badgeAwarded')
      }
    }).catch(() => {
      this.setState({ backendErrors: { errorMessage: 'Something went wrong please try again later.', messageType: 'error' }, loading: false })
    })
  }

  handleErrors (isNotBadgeSelected: boolean, noUserSelected: boolean, userLimitExceeded: boolean) {
    if (isNotBadgeSelected) {
      this.setState({ errors: { ...this.state.errors, badge: errorMessages.noBadge } })
    }

    if (noUserSelected) {
      this.setState({ errors: { ...this.state.errors, usersInput: errorMessages.noContent } })
    }

    if (userLimitExceeded) {
      this.setState({ errors: { ...this.state.errors, usersInput: errorMessages.userLimitExceeded } })
    }
  }

  trackBadgeAwardedEvent (selectedBadgeID: string, awardedId: number | string, awardedUsername: string, awardedRole: string | undefined, numberOfUsersAwarded: number) {
    const { badgesList } = this.state
    const { awarderId, awarderUsername, awarderRole } = this.props

    if (!badgesList) return

    const awardedBadge = badgesList.find((badge) => badge.id.toString() === selectedBadgeID)

    if (!awardedBadge) return

    tracker.trackEvent('userevent', {
      type: 'Badge Awarded',
      badgeId: awardedBadge.id,
      badgeName: awardedBadge.title,
      badgeDescription: awardedBadge.description,
      awarderId: awarderId,
      awarderUsername: awarderUsername,
      awarderRole: awarderRole,
      awardedId: awardedId,
      awardedUsername: awardedUsername,
      awardedRole: awardedRole,
      numberOfUsersAwarded: numberOfUsersAwarded
    })
  }

  onUsersChange (users: Array<User>) {
    this.setState({ users, errors: { ...this.state.errors, usersInput: undefined } })
  }

  onBadgeSelected (badgeId: string) {
    this.setState({ selectedBadge: badgeId, errors: { ...this.state.errors, badge: undefined } })
  }

  render (props: Props, state: State) {
    if (!state.badgesList) return null
    return <div>
      {state.loading && <Loader />}
      {state.backendErrors.errorMessage && state.backendErrors.messageType && <AlertComponent
        messageType={state.backendErrors.messageType}
        message={state.backendErrors.errorMessage}
        sticky
      />}
      <Header
        title='Award badge to users'
        disableSaveButton
        disableCancelButton
        customButtons={[
          {
            title: 'Cancel',
            variant: 'secondary',
            action: () => window.location.assign('/settings/badge/overview')
          },
          {
            title: 'Award',
            variant: 'primary',
            action: this.awardBadge
          }
        ]}
      />
      <div class='award-badge'>
        <Panel customClass='award-badge--panel'>
          <div>
            <label>Badge</label>
            <BadgeSelector
              badges={state.badgesList}
              selected={state.badgesList.find((badge) => badge.id.toString() === state.selectedBadge)}
              onChange={this.onBadgeSelected}
              error={state.errors.badge}
            />
          </div>
          <br />
          <div>
            <label>To</label>
            <p>{`Choose one or more (max. ${MAX_NUMBER_USERS}) users. Separate multiple users by comma. Example: John, Jane, Jack`}</p>
            <UserSelector
              url='/member/getUsers?query='
              onChange={(users: Array<User>) => this.onUsersChange(users)}
              error={!!state.errors.usersInput}
            />
            {state.errors.usersInput && <span class='error'>{state.errors.usersInput}</span>}
          </div>
        </Panel>
      </div>
    </div>
  }
}

export default AwardBadge
